import type { SseJobEvent } from '@/types';
import { listenSse } from './listenSse';

/** The stream ended without the job's terminal event: a backend error event, a timeout, or a lost connection. */
export class SseJobError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SseJobError';
    }
}

type ListenParams = Parameters<typeof listenSse>[0];

/**
 * Runs one backend job end to end: opens the stream BEFORE `start` fires the
 * request (the channel can't replay), waits for the terminal event, and tears
 * the stream down whether or not `start` threw. Resolves with `start`'s result
 * only if the job completed; a job that reported an error, timed out, or lost
 * its connection rejects with `SseJobError`, so the mutation's state says so
 * rather than reporting a failed job as a success.
 */
export async function runSseJob<T>({
    start,
    onSseEvent,
    onSseError,
    onSseComplete,
    ...listen
}: Omit<ListenParams, 'signal'> & { start: () => Promise<T> }): Promise<T> {
    let completed = false;
    let failure: string | null = null;
    const sseAbort = new AbortController();

    try {
        const stream = listenSse({
            ...listen,
            signal: sseAbort.signal,
            onSseEvent: (event: SseJobEvent) => {
                if (event.type === 'event' && event.status === 'error') failure = event.message;
                onSseEvent(event);
            },
            onSseError: error => {
                failure = error;
                onSseError(error);
            },
            onSseComplete: () => {
                completed = true;
                onSseComplete();
            },
        });

        const result = await start();
        await stream;

        if (!completed) throw new SseJobError(failure ?? 'SSE stream ended before the job completed');
        return result;
    } finally {
        // No-op when the stream already ended; tears it down if `start` threw.
        sseAbort.abort();
    }
}
